/**
 * Utility functions for working with partisan scores stored in the database.
 * The database stores a signed score from -100 (Democratic) to +100 (Republican).
 * This module provides functions to derive direction and label from the stored score.
 */

/**
 * Calculate partisan direction from signed score
 * @param {number} signedScore - Signed score from -100 to +100
 * @returns {string} - 'Democratic', 'Republican', or 'Neutral'
 */
export function getPartisanDirection(signedScore) {
  if (signedScore === null || signedScore === undefined) return 'Neutral';
  if (signedScore < 0) return 'Democratic';
  if (signedScore > 0) return 'Republican';
  return 'Neutral';
}

/**
 * Calculate partisan label from signed score
 * @param {number} signedScore - Signed score from -100 to +100
 * @returns {string} - Label like 'Bipartisan', 'Leaning Democrat', etc.
 */
export function getPartisanLabel(signedScore) {
  if (signedScore === null || signedScore === undefined) return 'No Data';
  
  const absoluteScore = Math.abs(signedScore);
  const direction = getPartisanDirection(signedScore);
  
  // Only absolute values 0-10 are bipartisan
  if (absoluteScore <= 10) {
    return 'Bipartisan';
  } else if (absoluteScore <= 30) {
    if (direction === 'Democratic') {
      return 'Leaning Democrat';
    } else if (direction === 'Republican') {
      return 'Leaning Republican';
    } else {
      return 'Leaning Neutral';
    }
  } else if (absoluteScore <= 60) {
    if (direction === 'Democratic') {
      return 'Moderately Democrat';
    } else if (direction === 'Republican') {
      return 'Moderately Republican';
    } else {
      return 'Mixed Partisan';
    }
  } else if (absoluteScore <= 80) {
    if (direction === 'Democratic') {
      return 'Strongly Democrat';
    } else if (direction === 'Republican') {
      return 'Strongly Republican';
    } else {
      return 'Mixed Partisan';
    }
  } else {
    if (direction === 'Democratic') {
      return 'Highly Democrat';
    } else if (direction === 'Republican') {
      return 'Highly Republican';
    } else {
      return 'Mixed Partisan';
    }
  }
}

/**
 * Get complete partisan data from stored score
 * @param {number} partisanScore - Signed score from database (-100 to +100)
 * @returns {Object} - { signedScore, score, label, direction }
 */
export function getPartisanDataFromScore(partisanScore) {
  if (partisanScore === null || partisanScore === undefined) {
    return {
      signedScore: 0,
      score: 0,
      label: 'No Data',
      direction: 'Neutral'
    };
  }
  
  const signedScore = typeof partisanScore === 'number' ? partisanScore : parseFloat(partisanScore);
  const absoluteScore = Math.abs(signedScore);
  
  return {
    signedScore: signedScore,
    score: absoluteScore,
    label: getPartisanLabel(signedScore),
    direction: getPartisanDirection(signedScore)
  };
}

/**
 * Calculate sponsor and vote breakdowns from bill data
 * @param {Object} bill - Bill object with sponsors and vote_breakdowns
 * @returns {Object} - Breakdown data including sponsor counts, vote percentages, and scores
 */
export function calculatePartisanBreakdowns(bill) {
  // Calculate Sponsor Breakdown
  let democratSponsors = 0;
  let republicanSponsors = 0;
  let sponsorScore = 0;
  
  if (bill.sponsors && bill.sponsors.length > 0) {
    bill.sponsors.forEach(s => {
      const party = (s.party || '').toUpperCase();
      if (party === 'D' || party === 'DEM' || party === 'DEMOCRAT' || party === 'DEMOCRATIC') {
        democratSponsors++;
      } else if (party === 'R' || party === 'REP' || party === 'REPUBLICAN') {
        republicanSponsors++;
      }
    });
    
    const total = democratSponsors + republicanSponsors;
    if (total > 0) {
      const democratPercent = (democratSponsors / total) * 100;
      const republicanPercent = (republicanSponsors / total) * 100;
      // Signed score: negative for Democratic, positive for Republican
      sponsorScore = republicanPercent - democratPercent;
    }
  }
  
  // Find Final Major Floor Vote
  let finalVote = null;
  if (bill.vote_breakdowns && bill.vote_breakdowns.length > 0) {
    // Find votes with final passage/third reading/passed
    const finalVotes = bill.vote_breakdowns.filter(vb => {
      const desc = (vb.description || '').toLowerCase();
      return desc.includes('final passage') || desc.includes('third reading') || desc.includes('passed');
    });
    
    // Use the latest one if multiple
    if (finalVotes.length > 0) {
      finalVote = finalVotes.reduce((latest, vb) => {
        if (!latest) return vb;
        const d1 = new Date(latest.date || 0);
        const d2 = new Date(vb.date || 0);
        return d2 > d1 ? vb : latest;
      }, null);
    }
    
    // If not found, fallback to most recent vote
    if (!finalVote) {
      finalVote = bill.vote_breakdowns.reduce((latest, vb) => {
        if (!latest) return vb;
        const d1 = new Date(latest.date || 0);
        const d2 = new Date(vb.date || 0);
        return d2 > d1 ? vb : latest;
      }, null);
    }
  }
  
  // Calculate Vote Breakdown
  let voteScore = 0;
  let demYeaPercent = 0;
  let repYeaPercent = 0;
  let hasVoteData = false;
  
  if (finalVote && finalVote.partyVoteCounts) {
    const dem = finalVote.partyVoteCounts.Democrat || {};
    const rep = finalVote.partyVoteCounts.Republican || {};
    const demTotal = (dem.Yea || 0) + (dem.Nay || 0) + (dem.NV || 0) + (dem.Absent || 0) + (dem.Excused || 0);
    const repTotal = (rep.Yea || 0) + (rep.Nay || 0) + (rep.NV || 0) + (rep.Absent || 0) + (rep.Excused || 0);
    
    if (demTotal > 0 && repTotal > 0) {
      demYeaPercent = (dem.Yea || 0) / demTotal * 100;
      repYeaPercent = (rep.Yea || 0) / repTotal * 100;
      // Signed score: negative for Democratic, positive for Republican
      voteScore = repYeaPercent - demYeaPercent;
      hasVoteData = true;
    }
  }
  
  return {
    democratSponsors,
    republicanSponsors,
    sponsorScore: Math.round(sponsorScore * 10) / 10,
    voteScore: hasVoteData ? Math.round(voteScore * 10) / 10 : null,
    demYeaPercent: hasVoteData ? Math.round(demYeaPercent * 10) / 10 : null,
    repYeaPercent: hasVoteData ? Math.round(repYeaPercent * 10) / 10 : null,
    hasVoteData
  };
}

